<template>
  <article
    class="live-card"
    role="button"
    tabindex="0"
    :aria-label="`Open live: ${publication.text}`"
    @click="handleOpen"
    @keydown.enter="handleOpen"
    @keydown.space.prevent="handleOpen"
  >
    <!-- Thumbnail -->
    <div class="thumb" :style="thumbStyle">
      <div class="thumb__top">
        <!-- <span v-if="isLive" class="badge badge--live"> -->
        <span class="badge badge--live">
          <span class="badge--live__dot"></span>LIVE
        </span>
    <!--     <span v-else-if="isUpcoming" class="badge badge--upcoming">
          Starts {{ startsInLabel }}
        </span>
        <span v-else class="badge badge--ended">Replay · {{ publication.stats?.duration ?? 0 }}</span>
 -->
        <span class="badge badge--type" :title="publication.type === 'private' ? 'Private room' : 'Public room'">
          <svg v-if="publication.type === 'private'" class="type-icon" viewBox="0 0 24 24" fill="none">
            <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" stroke-width="2"/>
            <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" stroke-width="2"/>
          </svg>
          <svg v-else class="type-icon" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
            <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3Z" stroke="currentColor" stroke-width="2"/>
          </svg>
        </span>
      </div>

      <div class="thumb__bottom">
        <div class="participants-pill">
          <span class="participants-pill__avatars">
            <span
              v-for="actor in visibleActors"
              :key="actor.userId"
              class="mini-avatar"
              :style="{ background: avatarColor(actor.userId) }"
            ></span>
          </span>
          <span class="participants-pill__count">{{ formatCount(publication.actors?.length) }}</span>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="body">
      <div class="host-row">
        <span class="host-avatar" :style="{ background: userInfos.profilImgMini1 ? `url(${store.state.baseUrl}images/${userInfos.profilImgMini1}) no-repeat center / cover`: avatarColor(userInfos.pseudo) }">
        </span>
        <div class="host-meta">
          <span class="host-name">{{ userInfos.pseudo }}</span>
          <span class="host-sub">Hosting · {{ publication.actors.length }} on stage</span>
        </div>
      </div>

      <p class="description">{{ publication.text }}</p>

      <ul v-if="publication.tags?.length" class="tags">
        <li v-for="tag in publication.tags.slice(0, 3)" :key="tag" class="tag">#{{ tag }}</li>
        <li v-if="publication.tags.length > 3" class="tag tag--muted">+{{ publication.tags.length - 3 }}</li>
      </ul>

      <div class="stats">
        <span class="stat" title="Views">
          <svg class="stat-icon" viewBox="0 0 24 24" fill="none"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>
          <span class="stat__value">{{ formatCount(publication.stats?.views) }}</span>
        </span>
        <span class="stat" title="Likes">
          <svg class="stat-icon" viewBox="0 0 24 24" fill="none"><path d="M12 20.5s-7.5-4.6-9.7-9.3C.7 7.9 2.6 4.5 6 4.5c2 0 3.4 1 4 2.4.6-1.4 2-2.4 4-2.4 3.4 0 5.3 3.4 3.7 6.7C19.5 15.9 12 20.5 12 20.5Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
          <span class="stat__value">{{ formatCount(publication.stats?.likes) }}</span>
        </span>
        <span class="stat" title="Comments">
          <svg class="stat-icon" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v12H8l-4 4V4Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
          <span class="stat__value">{{ formatCount(publication.stats?.comments) }}</span>
        </span>
        <span class="stat" title="Shares">
          <svg class="stat-icon" viewBox="0 0 24 24" fill="none"><path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2v14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span class="stat__value">{{ formatCount(publication.stats?.shares) }}</span>
        </span>
      </div>
    </div>
  </article>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const props = defineProps({
  publication: {
    type: Object,
    required: true
  }
})

const userInfos = computed(() => store.state.users[props.publication.userId] || {})
/* const bgStyle = computed(() => {
  if (!props.infos.img) return { backgroundImage: `url(${require('./img/publication.svg')})` }
  return { backgroundImage: `url(${store.state.baseUrl}images/${props.publication.img})` }
}) */

const emit = defineEmits(['enter-live'])

function handleOpen() {
  emit('enter-live', props.publication)
}

/* ---------- Live status ---------- */
/* const now = Date.now()


const isLive = computed(() => {
  const { start, end } = props.publication
  if (!start) return false
  const s = new Date(start).getTime()
  const e = end ? new Date(end).getTime() : null
  return s <= now && (e === null || e >= now)
})

 const isUpcoming = computed(() => {
  const { start } = props.publication
  if (!start) return false
  return new Date(start).getTime() > now
})

const startsInLabel = computed(() => {
  const diffMs = new Date(props.publication.start).getTime() - now
  const mins = Math.round(diffMs / 60000)
  if (mins < 60) return `in ${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `in ${hours}h`
  return `in ${Math.round(hours / 24)}d`
}) */

/* ---------- Thumbnail ---------- */
const thumbStyle = computed(() => ({
  backgroundImage: `linear-gradient(rgba(10,14,26,0.15), rgba(10,14,26,0.75)), url(${store.state.baseUrl}images/${props.publication.img})`
}))

/* ---------- Actors / avatars ---------- */
const visibleActors = computed(() => (props.publication.actors || []).slice(0, 4))

const avatarPalette = [
  'linear-gradient(135deg,#38bdf8,#6366f1)',
  'linear-gradient(135deg,#ec4899,#8b5cf6)',
  'linear-gradient(135deg,#22d3ee,#0ea5e9)',
  'linear-gradient(135deg,#f97316,#ec4899)',
  'linear-gradient(135deg,#a78bfa,#38bdf8)'
]

function hashString(str = '') {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

function avatarColor(userId) {
  return avatarPalette[hashString(userId) % avatarPalette.length]
}

/* function initials(pseudo = '') {
  return pseudo.trim().slice(0, 2).toUpperCase()
} */

/* ---------- Formatting ---------- */
function formatCount(n = 0) {
  if(!n) return 0
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return String(n)
}
</script>

<style scoped>
.live-card {
  --bg-card: #10162a;
  --bg-card-hover: #141b33;
  --border: rgba(148, 163, 184, 0.09);
  --cyan: #22d3ee;
  --magenta: #ec4899;
  --violet: #8b5cf6;
  --live: #fb3d68;
  --text-primary: #e7ebf5;
  --text-secondary: #8592ad;
  --text-muted: #4b5670;

  font-family: 'Inter', 'Sora', system-ui, -apple-system, sans-serif;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  width: 100%;
  max-width: 380px;
  text-align: left;
  color: var(--text-primary);
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  height: fit-content;
  flex-shrink: 0;
  margin-bottom: 0px;
}

.live-card:hover,
.live-card:focus-visible {
  transform: translateY(-3px);
  background: var(--bg-card-hover);
  border-color: rgba(34, 211, 238, 0.28);
}

.live-card:focus-visible {
  outline: 2px solid var(--cyan);
  outline-offset: 2px;
}

.live-card--live {
  border-color: rgba(251, 61, 104, 0.25);
}

/* ---- Thumbnail ---- */
.thumb {
  position: relative;
  aspect-ratio: 16 / 9;
  min-height: 200px;
  background-size: cover;
  background-position: center;
  background-color: #0c1020;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 12px;
}

.thumb__top {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.badge {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 4px 9px;
  border-radius: 999px;
  backdrop-filter: blur(6px);
  color: var(--text-primary);
  background: rgba(10, 14, 26, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.badge--live {
  color: #fff;
  background: rgba(251, 61, 104, 0.16);
  border-color: rgba(251, 61, 104, 0.5);
}

.badge--live__dot {
  width: 6px;
  height: 6px;
  margin-right: 5px;
  border-radius: 50%;
  background: var(--live);
  box-shadow: 0 0 0 0 rgba(251, 61, 104, 0.6);
  /* animation: pulse-dot 1.6s infinite; */
}

/* @keyframes pulse-dot {
  0% { box-shadow: 0 0 0 0 rgba(251, 61, 104, 0.55); }
  70% { box-shadow: 0 0 0 6px rgba(251, 61, 104, 0); }
  100% { box-shadow: 0 0 0 0 rgba(251, 61, 104, 0); }
} */

.badge--upcoming { color: #cdd6e8; }
.badge--ended { color: #9aa5bd; }

.badge--type {
  padding: 5px;
  color: var(--cyan);
}

.type-icon {
  width: 13px;
  height: 13px;
  display: block;
}

.thumb__bottom {
  position: relative;
  display: flex;
  flex-direction: column;
}

.participants-pill {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
  background: rgba(10, 14, 26, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 4px 9px 4px 4px;
  border-radius: 999px;
  backdrop-filter: blur(6px);
}

.participants-pill__avatars {
  display: flex;
  margin-right: 6px;
}

.participants-pill__count {
  display: inline-block;
}

.mini-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid #0c1020;
  margin-left: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
}

.mini-avatar:first-child { margin-left: 0; }

/* ---- Body ---- */
.body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
}

.body > * {
  margin-top: 10px;
}

.body > *:first-child {
  margin-top: 0;
}

.host-row {
  display: flex;
  align-items: center;
}

.host-avatar {
  width: 38px;
  height: 38px;
  margin-right: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background-size: cover;
  background-position: center;
  background: #22d3ee;
}

.host-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 0;
}

.host-name {
  font-weight: 600;
  font-size: 15px;
  font-family: 'Sora', 'Inter', sans-serif;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.host-sub {
  font-size: 12.5px;
  color: var(--text-muted);
}

.description {
  margin: 10px 0 0;
  font-size: 14.5px;
  line-height: 1.45;
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tags {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  margin: 10px 0 0;
  padding: 0;
}

.tag {
  font-size: 12px;
  font-weight: 600;
  color: var(--cyan);
  background: rgba(34, 211, 238, 0.09);
  border: 1px solid rgba(34, 211, 238, 0.18);
  padding: 3px 8px;
  border-radius: 999px;
  margin-right: 6px;
  margin-bottom: 6px;
}

.tag--muted {
  color: var(--text-muted);
  background: rgba(148, 163, 184, 0.06);
  border-color: var(--border);
}

.stats {
  display: flex;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid var(--border);
  font-variant-numeric: tabular-nums;
}

.stat {
  display: inline-flex;
  align-items: center;
  margin-right: 14px;
  font-size: 13px;
  color: var(--text-muted);
}

.stat:last-child {
  margin-right: 0;
}

.stat-icon {
  width: 16px;
  height: 16px;
  margin-right: 4px;
  opacity: 0.8;
}

/* ---- Compact sizing for very small screens only ---- */
@media (max-width: 340px) {
  .live-card {
    margin-bottom: 10px;    
  }
  .badge {
    font-size: 11px;
  }

  .type-icon {
    width: 11px;
    height: 11px;
  }

  .participants-pill {
    font-size: 11.5px;
  }

  .mini-avatar {
    width: 18px;
    height: 18px;
    font-size: 8px;
  }

  .host-avatar {
    width: 34px;
    height: 34px;
    font-size: 12px;
  }

  .host-name {
    font-size: 13.5px;
  }

  .host-sub {
    font-size: 11.5px;
  }

  .description {
    font-size: 13.5px;
  }

  .tag {
    font-size: 11px;
  }

  .stat {
    font-size: 12px;
  }

  .stat-icon {
    width: 14px;
    height: 14px;
  }
}
</style>